import {
	type MarketRegionsCreateDto,
	postMarketsMarketIdRegions,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { supabase } from "Infrastructure/Supabase/supabaseClient";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getMarketRegionListQueryKey } from "../Query/useMarketRegionList";

const marketMutationsResource = Resources.base.market.mutations;

export const useCreateRegionMutation = (marketId: number) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: MarketRegionsCreateDto) =>
			processResponse((d: MarketRegionsCreateDto) =>
				postMarketsMarketIdRegions(d, marketId),
			)(data),
		onError: (error) => {
			console.error(`${t(marketMutationsResource.createRegionsError)}:`, error);
		},
		onSuccess: async (_data, variables) => {
			if (variables.regionCode) {
				// takes the first 2 letters of the region code to derive the country code
				const countryCode = variables.regionCode.slice(0, 2).toUpperCase();
				const { data: countryData, error: countryError } = await supabase
					.from("countries")
					.select("id")
					.ilike("code", countryCode)
					.single();

				if (countryError) {
					console.error("Failed to fetch country from Supabase:", countryError);
					toast.warning("Region created, but transport country sync failed.");
				} else if (countryData) {
					const { error: cityError } = await supabase.from("cities").insert({
						name: variables.name,
						country_id: countryData.id,
						city_code: variables.regionCode,
						is_active: true,
					});

					if (cityError) {
						console.error("Failed to create city in Supabase:", cityError);
						toast.warning("Region created, but transport city sync failed.");
					}
				}
			}

			queryClient.invalidateQueries({
				queryKey: getMarketRegionListQueryKey(marketId),
			});
			toast.success(t(marketMutationsResource.createRegionsSuccess));
		},
	});
};
